import React, { memo } from 'react';

import { LegacyMetricGauge } from './LegacyMetricGauge';
import { TieredMetricGauge } from './TieredMetricGauge';

export type GaugeVariant = 'legacy' | 'tiered';

interface MetricGaugeVisualProps {
    variant: GaugeVariant;
    valueText: string;
    colorIndex: number;
    metricOmitted?: boolean;
    // legacy-only
    value?: number;
    target?: number;
    metricAttained?: boolean;
    // tiered-only - driven by attainment_rank (0=Below Meets, 1=Meets, 2=Exceeds), not the raw
    // metric_attainment_level_id, so this stays correct if a future tier is added.
    attainmentRank?: number | null;
    // primary key for status color/icon per the design spec - see getTieredStatusVisual.
    attainmentLevelLabel?: string | null;
    // tiered-only - raw numbers for the 4-arc model. min/max come straight from the API and are
    // already oriented "worst->best" per metric_direction_id.
    metricValue?: number | null;
    min?: number;
    max?: number;
    meetsTarget?: number | null;
    exceedsTarget?: number | null;
}

/**
 * Pure dispatcher - deliberately has no rendering logic or constants of its own. variant must be
 * driven by scorecard_version.scorecard_calculation_rule_id upstream, never inferred from
 * whether tiered data happens to be present. The two gauges are fully separate components/files
 * (LegacyMetricGauge, TieredMetricGauge) with no shared constants, so a change to one's sizing or
 * stroke width can never bleed into the other.
 */
export const MetricGaugeVisual = memo(({
    variant,
    valueText,
    colorIndex,
    metricOmitted,
    value,
    target,
    metricAttained,
    attainmentRank,
    attainmentLevelLabel,
    metricValue,
    min,
    max,
    meetsTarget,
    exceedsTarget,
}: MetricGaugeVisualProps) => {
    if (variant === 'legacy') {
        return (
            <LegacyMetricGauge
                valueText={valueText}
                colorIndex={colorIndex}
                metricOmitted={metricOmitted}
                value={value}
                target={target}
                metricAttained={metricAttained}
            />
        );
    }

    return (
        <TieredMetricGauge
            valueText={valueText}
            colorIndex={colorIndex}
            metricOmitted={metricOmitted}
            attainmentRank={attainmentRank}
            attainmentLevelLabel={attainmentLevelLabel}
            metricValue={metricValue}
            min={min}
            max={max}
            meetsTarget={meetsTarget}
            exceedsTarget={exceedsTarget}
        />
    );
});
MetricGaugeVisual.displayName = 'MetricGaugeVisual';

export default MetricGaugeVisual;
